/* PlannerWorkspaceDeck — iter101.53
 *
 * The tab bar that sits below the PlannerCaseCommandHeader on the
 * planner case detail page. Extends the same visual DNA as the
 * command header (teal accents, caps kicker, icon discs, hairline
 * rules, soft depth) so the top of the page reads as one cohesive
 * command surface.
 *
 * Composition:
 *   ┌────────────────────────────────────────────────────────────┐
 *   │  WORKSPACE ·  [🩺]Doctor's Submission [✎]Author the plan   │
 *   │               [🔒]MFG [💬]Chat [📝]Notes [⏱]Timeline       │
 *   └────────────────────────────────────────────────────────────┘
 *
 * Each tab is a <DeckTrigger> which is a thin wrapper around
 * Radix TabsTrigger — the Radix state stays untouched so parent
 * `<Tabs value={tab} onValueChange={setTab}>` continues to work.
 *
 * Attention affordances (passed as props):
 *   · locked=true         → amber-tinted lock disc + desaturated label
 *   · unread=true         → cyan ping dot at the tab's top-right
 *   · badge="MFG PENDING" → tiny amber caps chip inline after label
 *   · pulseDot=true       → amber ping dot inline after label
 */
import React from "react";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Lock } from "@phosphor-icons/react";


/* Individual tab pill — icon-disc + label + optional attention chip.
 * Uses Radix's `data-state="active"` attribute (surfaced on the
 * trigger) to drive the active-state styling on both the pill and
 * the icon disc via Tailwind's `group-data-[state=active]:` variants. */
function DeckTrigger({
    value,
    icon: Icon,
    label,
    locked = false,
    unread = false,
    badge = null,
    pulseDot = false,
    testid,
}) {
    const iconTone = locked ? "amber" : "teal";
    return (
        <TabsTrigger
            value={value}
            data-testid={testid}
            className={[
                // iter101.58 — Each tab now flexes to fill equal
                // width. Content is centered so the pill stays
                // balanced when the label is short. Removed
                // shrink-0 so triggers actually stretch.
                "group relative inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-2.5 py-1.5",
                "text-[12.5px] font-semibold text-slate-600 whitespace-nowrap",
                "transition-all duration-200 ease-out",
                "hover:bg-teal-50/60 hover:text-slate-800",
                // Active pill — soft gradient bg + teal text + micro elevation
                "data-[state=active]:bg-gradient-to-b data-[state=active]:from-teal-50 data-[state=active]:to-teal-100/70",
                "data-[state=active]:text-teal-900",
                "data-[state=active]:shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_2px_10px_-4px_rgba(20,184,166,0.32)]",
                "data-[state=active]:ring-1 data-[state=active]:ring-teal-200/70",
                locked ? "opacity-70" : "",
            ].join(" ")}
        >
            {/* Icon disc — teal-tinted when idle, solid teal gradient
                with white icon when active. Amber for locked tabs so
                the eye reads "not-yet-actionable" at a glance. */}
            <span
                className={[
                    "relative w-6 h-6 rounded-full grid place-items-center shrink-0",
                    "transition-all duration-200 ease-out",
                    "group-hover:scale-[1.04]",
                    // Idle tint
                    iconTone === "teal"
                        ? "bg-teal-50 border border-teal-200/60 text-teal-700"
                        : "bg-amber-50 border border-amber-200/70 text-amber-700",
                    // Active — gradient disc with white icon
                    "group-data-[state=active]:border-transparent",
                    "group-data-[state=active]:bg-gradient-to-br group-data-[state=active]:from-teal-500 group-data-[state=active]:to-teal-600",
                    "group-data-[state=active]:text-white",
                    "group-data-[state=active]:shadow-[inset_0_-1px_0_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.25)]",
                ].join(" ")}
                aria-hidden
            >
                <Icon size={13} weight={locked ? "fill" : "duotone"} className="group-data-[state=active]:duotone" />
            </span>

            <span className="leading-tight tracking-tight">{label}</span>

            {/* Amber "action pending" caps chip (e.g. MFG SPEC PENDING).
                Placed inline so it reads like a subordinate to the label. */}
            {badge && (
                <span
                    className="inline-flex items-center gap-1 px-1.5 h-4 rounded-full bg-amber-100 border border-amber-200/80 text-amber-800 text-[9px] font-bold tracking-[0.14em] uppercase leading-none"
                    data-testid={`${testid}-badge`}
                >
                    <span className="w-1 h-1 rounded-full bg-amber-500 animate-pulse" aria-hidden />
                    {badge}
                </span>
            )}

            {/* Amber inline pulse dot — used on MFG when the case just
                flipped to approved_pending_mfg but no full badge is
                warranted (kept for backward compatibility with the
                current attention affordance). */}
            {pulseDot && !badge && (
                <span
                    className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"
                    data-testid={`${testid}-dot`}
                    aria-hidden
                />
            )}

            {/* Cyan unread dot — top-right ping so it doesn't compete
                with the label width. */}
            {unread && (
                <span
                    className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-cyan-500 ring-2 ring-card"
                    title="Unread message from the doctor"
                    data-testid={`${testid}-unread-dot`}
                    aria-hidden
                >
                    <span className="absolute inset-0 rounded-full bg-cyan-400 animate-ping opacity-70" />
                </span>
            )}
        </TabsTrigger>
    );
}


/**
 * <PlannerWorkspaceDeck>
 *
 * Renders the whole TabsList surface with all triggers already wired.
 * Must be used INSIDE a parent `<Tabs value onValueChange>` — the
 * Radix Root stays in PlannerCaseDetail so state ownership doesn't
 * change.
 *
 * Props map to the affordances on each tab:
 *   iconSubmission / iconPlan / iconMfg / iconChat / iconNotes /
 *   iconTimeline                     — phosphor icon components
 *   unreadFromDoctor: bool           — cyan ping on Submission
 *   planLocked: bool                 — amber lock on Author-the-plan
 *   showMfgTab: bool                 — whether to render MFG at all
 *   mfgLocked: bool                  — amber lock on MFG
 *   mfgBadge: string|null            — "MFG SPEC PENDING" mini chip
 */
export default function PlannerWorkspaceDeck({
    icons,                    // { submission, plan, mfg, mfgLocked, chat, notes, timeline }
    unreadFromDoctor = false,
    planLocked = false,
    showMfgTab = false,
    mfgLocked = false,
    mfgBadge = null,
    mfgPulseDot = false,
}) {
    return (
        <TabsList
            data-testid="planner-case-tabslist"
            className={[
                "relative bg-card border border-border/70 rounded-2xl p-1.5 h-auto",
                "flex items-center gap-1 w-full flex-nowrap overflow-x-auto scrollbar-thin",
                "shadow-[0_1px_0_rgba(15,23,42,0.02),0_6px_18px_-14px_rgba(15,23,42,0.10)]",
                // Ambient teal wash on the left edge — matches the header's
                // top-left radial. Purely decorative, doesn't affect layout.
                "overflow-hidden",
            ].join(" ")}
            style={{
                backgroundImage: "linear-gradient(to right, rgba(20,184,166,0.045) 0%, transparent 40%)",
            }}
        >
            {/* Workspace caps kicker removed (iter101.58) — user
                found it visually indistinguishable from a real tab
                and kept trying to click it. The tab pills carry
                enough context on their own. */}

            <DeckTrigger
                value="submission"
                icon={icons.submission}
                label="Doctor's submission"
                unread={unreadFromDoctor}
                testid="planner-tab-submission"
            />
            <DeckTrigger
                value="plan"
                icon={planLocked ? Lock : icons.plan}
                label="Author the plan"
                locked={planLocked}
                testid="planner-tab-plan"
            />
            {showMfgTab && (
                <DeckTrigger
                    value="mfg"
                    icon={mfgLocked ? Lock : icons.mfg}
                    label="MFG Spec"
                    locked={mfgLocked}
                    badge={mfgBadge}
                    pulseDot={mfgPulseDot}
                    testid="planner-tab-mfg"
                />
            )}
            <DeckTrigger
                value="chat"
                icon={icons.chat}
                label="Provider Chat"
                testid="planner-tab-chat"
            />
            <DeckTrigger
                value="notes"
                icon={icons.notes}
                label="Notes"
                testid="planner-tab-notes"
            />
            <DeckTrigger
                value="timeline"
                icon={icons.timeline}
                label="Timeline"
                testid="planner-tab-timeline"
            />
        </TabsList>
    );
}
